import { useEffect, useMemo, useRef, useState } from "react";
import { useSafetyNavigate as useNavigate, SafetyLink as Link } from "@/lib/safetyNavigation";
import api, { API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { localIsoDay } from "@/lib/dates";
import { PageHeader, EmptyState } from "./_shared";
import {
  Plus, FileText, Lock, Trash2, ShieldCheck, Sparkles, Search, UploadCloud,
  Upload, Eye, Download, CheckCircle2, Clock, AlertCircle, PenLine, Briefcase, FileDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
  DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { SignaturePad } from "@/components/SignaturePad";
import { useFileDrop } from "@/lib/useFileDrop";
import { toast } from "sonner";

const OFFICE_ROLES = ["admin", "office", "super_admin", "foreman"];

function tok() { return localStorage.getItem("fl_token") || ""; }
function fileUrl(path, download) {
  return `${API}/files/view?path=${encodeURIComponent(path)}&auth=${encodeURIComponent(tok())}${download ? "&download=1" : ""}`;
}

export default function SSSPs() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [uploaded, setUploaded] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [q, setQ] = useState("");
  const isAdmin = ["admin", "super_admin"].includes(user.role);
  const canUpload = OFFICE_ROLES.includes(user.role);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/sssps"); setItems(r.data); }
    catch (e) { /* non-fatal */ }
    if (canUpload) {
      try { const r = await api.get("/job-sssp/docs"); setUploaded(r.data.docs || []); }
      catch (e) { /* non-fatal */ }
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const del = async (s) => {
    if (!window.confirm(`Delete the SSSP "${s.name}"?`)) return;
    try {
      await api.delete(`/sssps/${s.id}`);
      toast.success("SSSP deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    }
  };

  const merged = useMemo(() => {
    const built = items.map((s) => ({
      kind: "built", id: s.id, name: s.name, date: s.created_at, raw: s,
      searchable: [s.name, s.project_address, s.client_name, s.created_by_name],
    }));
    const up = uploaded.map((d) => ({
      kind: "uploaded", id: d.id, name: d.name, date: d.uploaded_at, raw: d,
      searchable: [d.name, d.job_title, d.uploaded_by_name],
    }));
    return [...built, ...up].sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [items, uploaded]);

  const ql = q.trim().toLowerCase();
  const shownItems = merged.filter((m) => !ql ||
    m.searchable.filter(Boolean).some((v) => String(v).toLowerCase().includes(ql)));

  const detailDoc = uploaded.find((d) => d.id === detailId) || null;

  return (
    <div data-testid="sssps-page" className="p-6 lg:p-10 max-w-6xl mx-auto pb-24 lg:pb-10">
      <PageHeader
        tag="/ NZ Site-Specific Safety Plans"
        title="SSSPs"
        subtitle="Build a Site-Specific Safety Plan, or upload your own and have the crew sign onto it."
        action={
          <div className="flex flex-wrap gap-2">
            {canUpload && (
              <Button variant="outline" onClick={() => setUploadOpen(true)}
                      className="rounded-sm border-zinc-900 dark:border-white/30 uppercase tracking-wider font-bold"
                      data-testid="upload-own-sssp-button">
                <Upload className="w-4 h-4 mr-2" /> Upload our own
              </Button>
            )}
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold" data-testid="new-sssp-button">
                  <Plus className="w-4 h-4 mr-2" /> New SSSP
                </Button>
              </DialogTrigger>
              <NewSSSPForm onCreated={() => { setOpen(false); load(); }} />
            </Dialog>
          </div>
        }
      />

      <div className="relative max-w-sm mb-4">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search plans, addresses, authors…"
               className="pl-9 w-full rounded-lg border-zinc-300 dark:border-white/15 bg-white dark:bg-[#101620]"
               data-testid="sssp-search-input" />
      </div>

      {loading ? (
        <div className="text-zinc-500 text-sm p-6">Loading…</div>
      ) : shownItems.length === 0 ? (
        <EmptyState
          icon={ShieldCheck}
          title={merged.length === 0 ? "No SSSPs yet" : "No matches"}
          subtitle={merged.length === 0 ? "Draft one with AI, start blank, or upload your own for the crew to sign." : "Try a different search."}
        />
      ) : (
        <div className="space-y-2">
          {shownItems.map((m) => m.kind === "built" ? (
            <BuiltRow key={`b-${m.id}`} s={m.raw} isAdmin={isAdmin} userId={user.id} onDelete={del} />
          ) : (
            <UploadedRow key={`u-${m.id}`} d={m.raw} onOpen={() => setDetailId(m.id)} />
          ))}
        </div>
      )}

      {canUpload && (
        <UploadOwnSSSPDialog open={uploadOpen} onOpenChange={setUploadOpen} onDone={load} />
      )}
      {detailDoc && (
        <UploadedSSSPDialog
          doc={detailDoc}
          canManage={canUpload}
          onOpenChange={(v) => !v && setDetailId(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

function BuiltRow({ s, isAdmin, userId, onDelete }) {
  return (
    <Link to={`/app/sssps/${s.id}`} className="hard-card flex items-center gap-4 px-4 py-3 hover:bg-zinc-50 dark:hover:bg-white/[0.03] transition group" data-testid={`sssp-card-${s.id}`}>
      <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-red-500/10 text-red-500">
        <ShieldCheck className="w-4 h-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="font-display font-bold uppercase tracking-tight truncate">{s.name}</div>
        <div className="text-xs text-zinc-500 mt-0.5 truncate">
          {s.project_address || "No address"} · By {s.created_by_name || "—"} · {new Date(s.created_at).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" })}
        </div>
      </div>
      {s.status === "locked" ? (
        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 bg-[#FF5F15] text-white rounded-full shrink-0">
          <Lock className="w-3 h-3" /> Locked
        </span>
      ) : (
        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 bg-zinc-100 dark:bg-white/10 border border-zinc-300 dark:border-white/10 rounded-full shrink-0">Draft</span>
      )}
      {(isAdmin || s.created_by === userId) && s.status !== "locked" && (
        <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(s); }}
                className="text-zinc-400 hover:text-red-600 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity shrink-0"
                data-testid={`sssp-delete-${s.id}`}>
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </Link>
  );
}

function UploadedRow({ d, onOpen }) {
  const assigned = typeof d.assigned_count === "number" ? d.assigned_count : null;
  const allSigned = assigned != null && assigned > 0 && d.signed_count >= assigned;
  return (
    <button onClick={onOpen} className="w-full text-left hard-card flex items-center gap-4 px-4 py-3 hover:bg-zinc-50 dark:hover:bg-white/[0.03] transition" data-testid={`sssp-uploaded-card-${d.id}`}>
      <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-[#FF5F15]/10 text-[#FF5F15]">
        <FileText className="w-4 h-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="font-display font-bold uppercase tracking-tight truncate flex items-center gap-2">
          {d.name}
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 bg-zinc-100 dark:bg-white/10 border border-zinc-300 dark:border-white/10 rounded-full">Uploaded</span>
        </div>
        <div className="text-xs text-zinc-500 mt-0.5 truncate flex items-center gap-1.5">
          {d.job_title ? <><Briefcase className="w-3 h-3" /> {d.job_title} · </> : <>Company-wide · </>}
          By {d.uploaded_by_name || "—"} · {new Date(d.uploaded_at).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" })}
        </div>
      </div>
      <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full shrink-0 ${allSigned ? "bg-emerald-500/15 text-emerald-600" : "bg-amber-500/15 text-amber-600"}`}>
        {allSigned ? <CheckCircle2 className="w-3 h-3" /> : <PenLine className="w-3 h-3" />}
        {d.signed_count}{assigned != null ? ` / ${assigned}` : ""} signed
      </span>
    </button>
  );
}

function NewSSSPForm({ onCreated }) {
  const nav = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [existingThisWeek, setExistingThisWeek] = useState({}); // {job_id: sssp}
  const [form, setForm] = useState({ job_id: "__custom__", name: "", project_address: "", scope_of_works: "", client_name: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  useEffect(() => {
    (async () => {
      try {
        const [jobsRes, ssspsRes] = await Promise.all([api.get("/jobs"), api.get("/sssps")]);
        setJobs(jobsRes.data.filter((j) => j.status !== "completed"));
        const weekStart = isoWeekMonday();
        const map = {};
        for (const s of ssspsRes.data) {
          if (s.job_id && s.iso_week_start === weekStart) map[s.job_id] = s;
        }
        setExistingThisWeek(map);
      } catch {/* non-fatal */}
    })();
  }, []);

  const pickJob = (jobId) => {
    if (jobId === "__custom__") {
      setForm({ job_id: "__custom__", name: "", project_address: "", scope_of_works: "", client_name: "" });
      return;
    }
    const j = jobs.find((x) => x.id === jobId);
    if (!j) return;
    setForm({
      job_id: j.id,
      name: j.title || "",
      project_address: j.site_address || "",
      client_name: j.client_name || "",
      scope_of_works: j.description || "",
    });
  };

  const dupeWarning = form.job_id !== "__custom__" ? existingThisWeek[form.job_id] : null;

  const submit = async (useAi) => {
    if (!form.name.trim()) { toast.error("Project name required"); return; }
    setBusy(true);
    try {
      const payload = { ...form, use_ai: useAi };
      if (payload.job_id === "__custom__") payload.job_id = null;
      const { data } = await api.post("/sssps", payload);
      toast.success(useAi ? "AI draft ready — review and edit" : "Blank SSSP created");
      onCreated(data);
      nav(`/app/sssps/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create SSSP");
    } finally { setBusy(false); }
  };

  return (
    <DialogContent className="rounded-sm">
      <DialogHeader>
        <DialogTitle className="font-display uppercase tracking-tight text-2xl">New Site-Specific Safety Plan</DialogTitle>
        <DialogDescription>Pick a job to auto-fill the basics. One SSSP per job per week.</DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <Label className="label-tag">Project / Job *</Label>
          <Select value={form.job_id} onValueChange={pickJob}>
            <SelectTrigger className="mt-2 rounded-sm border-zinc-400 h-10" data-testid="new-sssp-job-select">
              <SelectValue placeholder="Select a job…" />
            </SelectTrigger>
            <SelectContent className="rounded-sm max-h-72">
              <SelectItem value="__custom__" data-testid="new-sssp-job-custom">Custom (no linked job)</SelectItem>
              {jobs.map((j) => {
                const taken = !!existingThisWeek[j.id];
                return (
                  <SelectItem key={j.id} value={j.id} disabled={taken} data-testid={`new-sssp-job-${j.id}`}>
                    {j.title}{taken ? "  · SSSP exists this week" : ""}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          {dupeWarning && (
            <p className="text-[11px] font-mono text-red-600 mt-1.5" data-testid="new-sssp-dupe-warning">
              An SSSP for this job already exists this week — &ldquo;{dupeWarning.name}&rdquo;. Open it from the list instead.
            </p>
          )}
        </div>
        <div>
          <Label className="label-tag">Project name *</Label>
          <Input value={form.name} onChange={set("name")} className="mt-2 rounded-sm border-zinc-400" placeholder="Lincoln Rd resurface" data-testid="new-sssp-name" />
        </div>
        <div>
          <Label className="label-tag">Site address</Label>
          <Input value={form.project_address} onChange={set("project_address")} className="mt-2 rounded-sm border-zinc-400" placeholder="Lincoln Rd, Henderson, Auckland" data-testid="new-sssp-address" />
        </div>
        <div>
          <Label className="label-tag">Client</Label>
          <Input value={form.client_name} onChange={set("client_name")} className="mt-2 rounded-sm border-zinc-400" placeholder="Auckland Transport" data-testid="new-sssp-client" />
        </div>
        <div>
          <Label className="label-tag">Scope of works</Label>
          <Textarea value={form.scope_of_works} onChange={set("scope_of_works")} rows={3} className="mt-2 rounded-sm border-zinc-400" placeholder="Mill and resurface 3km of urban road including kerb &amp; channel renewal" data-testid="new-sssp-scope" />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" disabled={busy || !!dupeWarning} onClick={() => submit(false)} className="rounded-sm uppercase tracking-wider font-bold" data-testid="new-sssp-blank">
          {busy ? "Creating…" : "Create blank"}
        </Button>
        <Button disabled={busy || !!dupeWarning} onClick={() => submit(true)} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold" data-testid="new-sssp-ai">
          <Sparkles className="w-4 h-4 mr-2" /> {busy ? "AI drafting…" : "AI draft"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function UploadOwnSSSPDialog({ open, onOpenChange, onDone }) {
  const inputRef = useRef(null);
  const [jobs, setJobs] = useState([]);
  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [jobId, setJobId] = useState("__none__");
  const [busy, setBusy] = useState(false);
  const { dragging, dropProps } = useFileDrop((files) => setFile(files?.[0] || null));

  useEffect(() => {
    if (!open) { setFile(null); setName(""); setJobId("__none__"); return; }
    api.get("/jobs").then((r) => setJobs((r.data || []).filter((j) => j.status !== "completed"))).catch(() => {});
  }, [open]);

  const submit = async () => {
    if (!file) return toast.error("Please choose a file");
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    if (name.trim()) fd.append("name", name.trim());
    if (jobId !== "__none__") fd.append("job_id", jobId);
    try {
      await api.post("/job-sssp/docs", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("SSSP uploaded — crew can now sign it");
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm" data-testid="upload-own-sssp-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl">Upload your own SSSP</DialogTitle>
          <DialogDescription>Use your company's own plan instead of building one. Link it to a job so the right crew signs it.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="label-tag">Plan name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-2 rounded-sm border-zinc-400" placeholder="Defaults to the file name" data-testid="upload-own-sssp-name" />
          </div>
          <div>
            <Label className="label-tag">Link to a job (optional)</Label>
            <Select value={jobId} onValueChange={setJobId}>
              <SelectTrigger className="mt-2 rounded-sm border-zinc-400 h-10" data-testid="upload-own-sssp-job-select">
                <SelectValue placeholder="No linked job…" />
              </SelectTrigger>
              <SelectContent className="rounded-sm max-h-72">
                <SelectItem value="__none__">Company-wide (no linked job)</SelectItem>
                {jobs.map((j) => (
                  <SelectItem key={j.id} value={j.id} data-testid={`upload-own-sssp-job-${j.id}`}>{j.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-zinc-500 mt-1.5">Linking a job tracks which dispatched crew still need to sign.</p>
          </div>
          <div>
            <Label className="label-tag">SSSP file</Label>
            <div
              {...dropProps}
              onClick={() => inputRef.current?.click()}
              className={`mt-2 flex flex-col items-center justify-center gap-2 rounded-sm border-2 border-dashed px-4 py-6 text-center cursor-pointer transition-colors ${
                dragging ? "border-[#FF5F15] bg-orange-50" : "border-zinc-300 dark:border-zinc-700 hover:border-[#FF5F15] hover:bg-orange-50/40"
              }`}
              data-testid="upload-own-sssp-dropzone"
            >
              <UploadCloud className={`w-7 h-7 ${dragging ? "text-[#FF5F15]" : "text-zinc-400"}`} />
              {file ? (
                <div className="text-sm font-medium text-zinc-900 dark:text-white flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-green-600" /> {file.name}
                </div>
              ) : (
                <div className="text-sm text-zinc-600 dark:text-zinc-300">
                  <span className="font-semibold text-[#FF5F15]">Drag &amp; drop</span> your SSSP, or <span className="underline">choose file</span>
                </div>
              )}
              <input ref={inputRef} type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp" className="hidden"
                     onChange={(e) => { setFile(e.target.files?.[0] || null); e.target.value = ""; }}
                     data-testid="upload-own-sssp-file-input" />
            </div>
            <p className="text-[11px] text-zinc-500 mt-1">PDF, image or Word doc · max 25MB</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm uppercase tracking-wider font-bold">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold" data-testid="upload-own-sssp-submit">
            {busy ? "Uploading…" : "Upload SSSP"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UploadedSSSPDialog({ doc, canManage, onOpenChange, onChanged }) {
  const [signing, setSigning] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState(doc.job_id || "__none__");
  const [savingJob, setSavingJob] = useState(false);
  const [jobsLoading, setJobsLoading] = useState(true);

  useEffect(() => { setJobId(doc.job_id || "__none__"); }, [doc.id, doc.job_id]);
  useEffect(() => {
    if (!canManage) return;
    api.get("/jobs")
      .then(({ data }) => setJobs((data || []).filter((j) => j.status !== "completed" || j.id === doc.job_id)))
      .catch(() => toast.error("Could not load jobs. Reopen this plan to try again."))
      .finally(() => setJobsLoading(false));
  }, [canManage, doc.job_id]);

  const saveJob = async () => {
    setSavingJob(true);
    try {
      await api.patch(`/job-sssp/docs/${doc.id}`, { job_id: jobId === "__none__" ? null : jobId });
      toast.success(jobId === "__none__" ? "SSSP is now company-wide" : "SSSP assigned to job");
      await onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update job assignment");
    } finally { setSavingJob(false); }
  };
  const assigned = typeof doc.assigned_count === "number" ? doc.assigned_count : null;

  const onSign = async (dataUrl) => {
    try {
      await api.post(`/job-sssp/docs/${doc.id}/sign`, { signature: dataUrl });
      toast.success("Signed — thanks");
      setSigning(false);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save signature"); }
  };

  const remove = async () => {
    if (!window.confirm(`Remove "${doc.name}"? Sign-offs will be hidden.`)) return;
    try { await api.delete(`/job-sssp/docs/${doc.id}`); toast.success("Removed"); onOpenChange(false); onChanged(); }
    catch { toast.error("Could not remove"); }
  };

  const downloadRecord = async () => {
    try {
      const res = await api.get(`/job-sssp/docs/${doc.id}/record.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `SSSP-Signed-${(doc.name || "record").replace(/[^A-Za-z0-9_-]+/g, "-")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(doc.job_id ? "Signed record generated & filed to the job" : "Signed record generated");
    } catch { toast.error("Could not generate record"); }
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-lg" data-testid="uploaded-sssp-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-xl">{doc.name}</DialogTitle>
          <DialogDescription>
            {doc.job_title ? `Linked to ${doc.job_title}` : "Company-wide SSSP"} · Uploaded by {doc.uploaded_by_name}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          <a href={fileUrl(doc.path)} target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-1.5 text-sm font-bold uppercase tracking-wider text-[#FF5F15] hover:underline" data-testid="uploaded-sssp-view">
            <Eye className="w-4 h-4" /> View SSSP
          </a>
          <a href={fileUrl(doc.path, true)}
             className="inline-flex items-center gap-1.5 text-sm font-bold uppercase tracking-wider text-zinc-600 dark:text-zinc-300 hover:text-[#FF5F15]" data-testid="uploaded-sssp-download">
            <Download className="w-4 h-4" /> Download
          </a>
          {!doc.my_signed && (
            <Button size="sm" onClick={() => setSigning(true)} className="ml-auto rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase text-[11px] font-bold tracking-wider h-8" data-testid="uploaded-sssp-sign">
              <PenLine className="w-3.5 h-3.5 mr-1" /> Read &amp; sign
            </Button>
          )}
          {doc.my_signed && (
            <span className="ml-auto inline-flex items-center gap-1 text-xs font-bold text-emerald-600"><CheckCircle2 className="w-4 h-4" /> You signed</span>
          )}
        </div>

        {canManage && (
          <div className="space-y-2">
            <Label className="label-tag">Linked job</Label>
            <Select value={jobId} onValueChange={setJobId} disabled={jobsLoading || savingJob}>
              <SelectTrigger data-testid="uploaded-sssp-job-select">
                <SelectValue placeholder="Select a job" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="__none__">Company-wide (no linked job)</SelectItem>
                {doc.job_id && !jobs.some((j) => j.id === doc.job_id) && (
                  <SelectItem value={doc.job_id}>{doc.job_title || "Current job"}</SelectItem>
                )}
                {jobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.title}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={saveJob} disabled={jobsLoading || savingJob || jobId === (doc.job_id || "__none__")}
                    data-testid="uploaded-sssp-save-job" className="rounded-sm">
              {savingJob ? "Saving…" : "Save job assignment"}
            </Button>
          </div>
        )}

        {canManage && (doc.signed_count > 0) && (
          <button onClick={downloadRecord}
                  className="inline-flex items-center gap-1.5 text-sm font-bold uppercase tracking-wider text-zinc-700 dark:text-zinc-300 hover:text-[#FF5F15]"
                  data-testid="uploaded-sssp-record">
            <FileDown className="w-4 h-4" /> Signed record (PDF){doc.job_id ? " · filed to job" : ""}
          </button>
        )}

        {signing && (
          <div className="mt-1">
            <SignaturePad testIdPrefix="uploaded-sssp-sig" onSave={onSign} onCancel={() => setSigning(false)} />
          </div>
        )}

        <div className="mt-2 border-t border-zinc-100 dark:border-white/10 pt-3">
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-400 font-bold mb-1.5">
            Signed off · {doc.signed_count}{assigned != null ? ` of ${assigned}` : ""}
          </div>
          {(!doc.signatures || doc.signatures.length === 0) ? (
            <p className="text-xs text-zinc-500">No one has signed yet.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {doc.signatures.map((s) => (
                <div key={s.user_id} className="flex items-center gap-1.5 text-xs bg-zinc-50 dark:bg-white/5 rounded-full pl-1 pr-2.5 py-1" data-testid={`uploaded-sssp-signer-${s.user_id}`}>
                  {s.signature_path
                    ? <img src={fileUrl(s.signature_path)} alt="" className="w-8 h-5 object-contain bg-white rounded-sm border border-zinc-200" />
                    : <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
                  <span className="font-medium text-zinc-800 dark:text-zinc-200">{s.user_name}</span>
                  <span className="text-zinc-400">{(s.signed_at || "").slice(0, 10)}</span>
                </div>
              ))}
            </div>
          )}

          {Array.isArray(doc.pending) && doc.job_id && (
            <div className="mt-3" data-testid="uploaded-sssp-pending-block">
              <div className="text-[10px] uppercase tracking-[0.18em] text-amber-600 font-bold mb-1.5 flex items-center gap-1">
                <Clock className="w-3 h-3" /> Not yet signed · {doc.pending_count ?? doc.pending.length}
              </div>
              {doc.pending.length === 0 ? (
                <p className="text-xs text-emerald-600 font-medium">
                  {assigned === 0 ? "No crew dispatched to this job yet." : "Everyone dispatched to this job has signed."}
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {doc.pending.map((p) => (
                    <div key={p.user_id} className="flex items-center gap-1.5 text-xs bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 rounded-full px-2.5 py-1 border border-amber-200 dark:border-amber-500/20" data-testid={`uploaded-sssp-pending-${p.user_id}`}>
                      <AlertCircle className="w-3.5 h-3.5" />
                      <span className="font-medium">{p.user_name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {canManage && (
          <DialogFooter>
            <Button variant="outline" onClick={remove} className="rounded-sm uppercase tracking-wider font-bold text-red-600 border-red-300 hover:bg-red-50" data-testid="uploaded-sssp-delete">
              <Trash2 className="w-4 h-4 mr-2" /> Remove
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

function isoWeekMonday() {
  const d = new Date();
  const day = (d.getDay() + 6) % 7; // 0 = Mon
  d.setDate(d.getDate() - day);
  return localIsoDay(d);
}
